/*
 * Before and after: a guest's real copy of the reminder (backend evidence.received) next to the same
 * copy with the requested sentence in it. Where it goes comes from the suggestion's position (the
 * backend checks the model's quote against this copy): a rewrite takes the quoted sentence's place,
 * an addition follows the quoted line. Cards without one fall back to the rules below. A preview of
 * the text only: ops place the change when they edit the template or the product info.
 */

// Lines that carry a topic today, in the languages operators send in.
const TOPIC_ANCHORS = {
  meeting_point: ['📍', 'meeting point', 'meet at', 'punto de encuentro', 'punto di incontro'],
  find_guide_on_the_day: ['📍', 'meeting point', 'meet at', 'punto de encuentro', 'punto di incontro'],
  transport_or_pickup: ['pickup', 'pick-up', 'pick up', 'recogida', '🚌', '📍', 'meeting point', 'punto de encuentro'],
  start_or_checkin_time: ['check-in', 'check in', '⏰', 'start', 'hora', 'orario'],
  duration_or_end_time: ['⏰', 'start', 'hora', 'orario'],
  tickets_voucher_or_entry: ['ticket', '🎟', 'voucher', 'boleto', 'entrada', 'biglietto'],
  app_or_link_problem: ['[link:'],
};

// A sign-off short enough to keep last ("See you soon!", "Regards,\nAmigo Tours").
const SIGN_OFF_CHARS = 60;

// The 24-hour and 1-hour reminders' notifications listen on aliases, not their flag codes (backend
// reminder_gaps.REMINDER_ALIASES).
const REMINDER_ALIASES = { 'tour_participant.trip_start': '24h', 'tour_participant.trip_about_to_begin': '1h' };

// The reminder the change belongs in: the one its position was checked against; else the template's
// own (trip_flag_event_2h: the 2-hour one; trip_start: the 24-hour one) or the product field's
// (reminders_20_hr: the 20-hour one); otherwise the one most questions followed.
export const previewCopy = (gap) => {
  const received = gap?.evidence?.received || [];
  const t = gap?.target || {};
  const field = /^reminders_(\d+)_hr$/.exec(t.attr || '');
  const kind = gap?.suggestion?.position?.reminder
    || REMINDER_ALIASES[t.event_code] || (t.event_code || '').split('trip_flag_event_')[1]
    || (field ? `${field[1]}h` : null);
  return received.find((r) => r.kind === kind) || received[0] || null;
};

// The copy split around the change -- { before, removed, added, after }: before + added + after is the
// copy with the change, before + removed + after the copy as it is ("removed" is empty for an
// addition) -- or null when there is nothing to show.
export const withChange = (copy, sentence, gap) => {
  const text = copy || '';
  const added = (sentence || '').trim();
  if (!text || !added || gap?.kind === 'app') return null;
  const pos = gap?.suggestion?.position || {};
  const quoted = pos.text ? text.indexOf(pos.text) : -1;

  // A rewrite: the new wording takes the quoted sentence's place.
  if (pos.type === 'replace' && quoted >= 0) {
    return { before: text.slice(0, quoted), removed: pos.text, added, after: text.slice(quoted + pos.text.length) };
  }

  // A product field the reminder prints today: the addition goes right after it.
  const current = (gap?.current_text || '').trim();
  const at = current.length >= 8 ? text.indexOf(current) : -1;
  if (at >= 0) {
    const end = at + current.length;
    return { before: `${text.slice(0, end)} `, removed: '', added, after: text.slice(end) };
  }

  // Where the suggestion says: after the quoted line (on a line of its own) or sentence.
  if (pos.type === 'after' && quoted >= 0) {
    const end = quoted + pos.text.length;
    const rest = text.slice(end);
    if (!rest || rest.startsWith('\n')) return { before: `${text.slice(0, end)}\n`, removed: '', added, after: rest };
    return { before: `${text.slice(0, end)} `, removed: '', added, after: /^\s/.test(rest) ? rest : ` ${rest}` };
  }

  // Otherwise on its own line, after the first line about the same topic...
  const lines = text.split('\n');
  const keys = TOPIC_ANCHORS[gap?.topic] || [];
  const idx = lines.findIndex((l) => keys.some((k) => l.toLowerCase().includes(k)));
  if (idx >= 0) {
    const rest = lines.slice(idx + 1);
    return { before: `${lines.slice(0, idx + 1).join('\n')}\n`, removed: '', added, after: rest.length ? `\n${rest.join('\n')}` : '' };
  }

  // ...or as its own paragraph, before a short sign-off when there is one.
  let last = lines.length - 1;
  while (last >= 0 && !lines[last].trim()) last -= 1;
  let start = last;
  while (start > 0 && lines[start - 1].trim()) start -= 1;
  const signOff = lines.slice(start, last + 1).join('\n');
  if (start > 0 && signOff.length <= SIGN_OFF_CHARS) {
    return { before: lines.slice(0, start).join('\n').replace(/\n*$/, '\n\n'), removed: '', added, after: `\n\n${lines.slice(start).join('\n')}` };
  }
  return { before: text.replace(/\n*$/, '\n\n'), removed: '', added, after: '' };
};
